import React from "react";
import Table, { ColumnsType } from "antd/lib/table"
import { WebApiCustomListsDtoCustomListItemDto, WebApiMemberDtoMemberDto, WebApiModelsMemberField, WebApiModelsCustomListField, WebApiServicesDataTransferObjectsGroupServiceGroupMemberDto, WebApiModelsMembershipProduct } from "../../Repository/eventbookyapi"
import { Button, Popconfirm, Space, TableProps } from "antd";
import MemberEditModal from "../Users/MemberEditModal";
import ManagePayments from "../Users/ManagePayments/ManagePayments";
import { DeleteOutlined } from "@ant-design/icons";
import { formatDateString, getPreviousYear, getUpcomingYear } from "../../Helpers/DateHelpers";
import { TextService } from "../../Services/TextService";
import { MemberService } from "../../Services/MemberService";
import { ComponentPropsWithoutRef, useEffect, useState } from "react";
import { CustomerPreferencesHelper } from '../../Helpers/CustomerPreferencesHelper';
import { PaymentConfiguration } from '../../Types/PaymentConfiguration';
import { SortOrder } from "antd/es/table/interface";
import { SavedSorting } from "../../Types/SavedSorting";
import { MemberFieldTypes } from "../../Types/MemberFieldTypes";
import CustomListEditModal from "../../Pages/CustomList/CustomListEditModal";
import CustomListAddModal from "../../Pages/CustomList/CustomListAddModal";
import { CompanyTypeConfiguration } from "../../Types/CompanyTypes";
import { useNavigate } from "react-router-dom";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { sortAlphabetically, sortDate, sortNumber } from "../../Helpers/TableSortHelper";

export interface MemberTableProps {
    savedSorting?: any;
    handlePageSizeChanged?: (size: number) => void;
    hasPingPaymentIntegration?: boolean;
    hasFortnoxIntegration?: boolean;
    pageSizePreference?: number;
    user?: any;
    style?: React.CSSProperties;
    isMail?: boolean;
    members?: any[];
    memberFieldSettings?: any[];
    membershipProducts?: WebApiModelsMembershipProduct[];
    groups?: any[];
    loadMembers?: () => void;
    removeMember?: (member: any) => void;
    enableRowClick?: boolean;
    showPaymentLinkColumnCurrentYear?: boolean;
    showPaymentLinkColumnNextYear?: boolean;
    showPaymentLinkColumnLastYear?: boolean;
}

export const MemberTable = (props: MemberTableProps) => {
    const navigate = useNavigate();

    // --- Modal state for custom list modals ---
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [addModalOpen, setAddModalOpen] = useState(false);
    const [editItem, setEditItem] = useState<WebApiCustomListsDtoCustomListItemDto | null>(null);
    const [modalField, setModalField] = useState<WebApiModelsMemberField | null>(null);
    const [modalMember, setModalMember] = useState<WebApiMemberDtoMemberDto | null>(null);
    const [modalCustomListId, setModalCustomListId] = useState<number | null>(null);
    const [loadingModal, setLoadingModal] = useState(false);
    const [loadingEditItem, setLoadingEditItem] = useState(false);
    const [customFieldsForModal, setCustomFieldsForModal] = useState<WebApiModelsCustomListField[]>([]);

    const GetText = TextService.GetText;
    const [memberToDelete, setMemberToDelete] = useState(0 as number);
    const [sortedInfo, setSortedInfo] = useState(undefined as SavedSorting | undefined);

    useEffect(() => {
        if (props.savedSorting) {
            setSortedInfo(props.savedSorting);
        };
    }, [props])

    const sortUniqueId = (a: any, b: any) => sortNumber(a.uniqueId, b.uniqueId);

    const onDeleteClick = (memberId: number) => {
        setMemberToDelete(memberId);
    };

    const handleOk = async () => {
        await MemberService.DeleteMember(memberToDelete);
        props.loadMembers && props.loadMembers();
        setMemberToDelete(0);
    };

    const onPaginateChange = (value: any, newPageSize: any) => {
        CustomerPreferencesHelper.setPageSize(newPageSize);
        props.handlePageSizeChanged?.(newPageSize);
    };


    const onTableChange = (pagination: any, filters: any, sorter: any, extra: any) => {
        if (sorter != null && sorter.field != null) {
            const sorting: SavedSorting = { columnKey: sorter.field, sortingDirection: sorter.order };
            CustomerPreferencesHelper.setSorter(sorting);
            setSortedInfo({ columnKey: sorter.field.toLowerCase(), sortingDirection: sorter.order });
        }
    }
    const sortIconClassName = 'ant-table-column-title';
    const HeaderCell = (props: ComponentPropsWithoutRef<'th'>) => {
        return (
            <th
                {...props}
                onClick={(e) => {
                    const currentTarget = e.currentTarget as HTMLElement;
                    const sortIconNode = currentTarget.querySelector(`.${sortIconClassName}`);
                    if (sortIconNode?.contains(e.target as HTMLElement)) {
                        props.onClick?.(e);
                    }
                }}
            />
        );
    };

    const components: TableProps['components'] = {
        header: {
            cell: HeaderCell
        }
    };

    const columns: ColumnsType<WebApiMemberDtoMemberDto> = [{
        title: GetText("MemberList_UniqueIdColumn"),
        width: 100,
        dataIndex: 'uniqueid',
        sortOrder: sortedInfo?.columnKey === 'uniqueid' ? sortedInfo?.sortingDirection as SortOrder : undefined,
        key: 'uniqueid',
        render: (_, member: WebApiMemberDtoMemberDto) => (
            <span>{member.uniqueId}</span>
        ),
        sorter: sortUniqueId,
        sortDirections: ['ascend', 'descend', 'ascend'],
    }]

    // Add company name column if user has CRM company type
    if (props.user?.companyType === CompanyTypeConfiguration.CRM) {
        columns.push({
            title: GetText("MemberList_CompanyColumn"),
            width: 150,
            dataIndex: 'company',
            key: 'company',
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.company}</span>
            ),
        });
    }

    const firstNameCol = {
        title: GetText("MemberList_FirstNameColumn"),
        dataIndex: 'firstname',
        key: 'firstname',
        sortOrder: sortedInfo?.columnKey === 'firstname' ? sortedInfo?.sortingDirection as SortOrder : undefined,
        width: 150,
        render: (_: any, member: WebApiMemberDtoMemberDto) => (
            <span>{member.firstName}<br />
            </span>
        ),
        sorter: { compare: (a: WebApiMemberDtoMemberDto, b: WebApiMemberDtoMemberDto) => sortAlphabetically(a, b, "firstName"), multiple: 2 },
        sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    }

    const lastNameCol = {
        title: GetText("MemberList_LastNameColumn"),
        dataIndex: 'lastname',
        key: 'lastname',
        sortOrder: sortedInfo?.columnKey === 'lastname' ? sortedInfo?.sortingDirection as SortOrder : undefined,
        width: 150,
        render: (_: any, member: WebApiMemberDtoMemberDto) => (
            <span>{member.lastName}<br />
            </span>
        ),
        sorter: { compare: (a: WebApiMemberDtoMemberDto, b: WebApiMemberDtoMemberDto) => sortAlphabetically(a, b, "lastName") },
        sortDirections: ['ascend', 'descend', 'ascend'] as SortOrder[],
    }

    const firstNameFieldColumnOrder = props.memberFieldSettings?.find((f: WebApiModelsMemberField) => f.columnType === MemberFieldTypes.FirstName)?.columnOrder;
    const lastNameFieldColumnOrder = props.memberFieldSettings?.find((f: WebApiModelsMemberField) => f.columnType === MemberFieldTypes.LastName)?.columnOrder;

    if (firstNameFieldColumnOrder < lastNameFieldColumnOrder) {
        columns.push(firstNameCol);
        columns.push(lastNameCol);
    } else {
        columns.push(lastNameCol);
        columns.push(firstNameCol);
    }

    columns.push(
        {
            title: GetText("MemberList_EmailColumn"),
            dataIndex: 'email',
            key: 'email',
            sortOrder: sortedInfo?.columnKey === 'email' ? sortedInfo?.sortingDirection as SortOrder : undefined,
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.email}<br /></span>
            ),
            sorter: { compare: (a, b) => sortAlphabetically(a, b, "email") },
            sortDirections: ['ascend', 'descend', 'ascend'],
        },
        {
            title: GetText("MemberList_PhoneColumn"),
            dataIndex: 'phone',
            key: 'phone',
            width: 150,
            sorter: false,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.phoneNumberFormatted}<br /></span>
            ),
        },
        {
            title: GetText("MemberList_GroupsColumn"),
            dataIndex: 'company',
            key: 'company',
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>
                    {member.groupNames?.map((groupName: any, i) => (
                        <>{groupName} <br /></>
                    ))}
                </span>
            ),
        }
    );

    // Add membership column only if there are membership products
    if (props.membershipProducts && props.membershipProducts.length > 0) {
        columns.push({
            title: GetText("MemberList_MembershipColumn"),
            dataIndex: 'membership',
            key: 'membership',
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => {
                const membershipNames: string[] = [];

                // Add primary membership product name
                if (member.membershipProduct) {
                    const membershipProduct = props.membershipProducts?.find(p => p.id === member.membershipProduct);
                    if (membershipProduct?.name) {
                        membershipNames.push(membershipProduct.name);
                    }
                }

                // Add addon membership product names
                if (member.membershipProductAddOn) {
                    member.membershipProductAddOn.forEach(addonId => {
                        const addonProduct = props.membershipProducts?.find(p => p.id === addonId);
                        if (addonProduct?.name) {
                            membershipNames.push(addonProduct.name);
                        }
                    });
                }

                return (
                    <span>
                        {membershipNames.map((membershipName, i) => (
                            <React.Fragment key={i}>
                                {membershipName} <br />
                            </React.Fragment>
                        ))}
                    </span>
                );
            },
        });
    }

    if (props.showPaymentLinkColumnCurrentYear) {
        columns.push({
            title: GetText("MemberList_PaymentLinkColumn") + " " + getPreviousYear(0),
            dataIndex: 'paymentLink',
            key: 'paymentLink',
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.paymentLink ? <a href={member.paymentLink} target="_blank" rel="noreferrer">{GetText("Common_Yes")}</a> : GetText("Common_No")}</span>
            ),
        });
    }

    if (props.showPaymentLinkColumnLastYear) {
        columns.push({
            title: GetText("MemberList_PaymentLinkColumn") + " " + getPreviousYear(1),
            dataIndex: 'paymentLink',
            key: 'paymentLink',
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.paymentLinkLastYear ? <a href={member.paymentLinkLastYear} target="_blank" rel="noreferrer">{GetText("Common_Yes")}</a> : GetText("Common_No")}</span>
            ),
        });
    }

    if (props.showPaymentLinkColumnNextYear) {
        columns.push({
            title: GetText("MemberList_PaymentLinkColumn") + " " + getUpcomingYear(1),
            dataIndex: 'paymentLink',
            key: 'paymentLink',
            width: 150,
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{member.paymentLinkNextYear ? <a href={member.paymentLinkNextYear} target="_blank" rel="noreferrer">{GetText("Common_Yes")}</a> : GetText("Common_No")}</span>
            ),
        });
    }

    if (props.isMail == true) {
        columns.push({
            title: GetText("MemberList_ActionColumn"),
            key: 'action',
            render: (_, member) => (
                <Space size="middle">
                    <Button size="small" onClick={() => props.removeMember?.(member)}>Ta bort</Button>
                </Space>
            )
        })
    }
    else {
        columns.push({
            title: GetText("MemberList_LastModifiedColumn"),
            dataIndex: 'lastmodified',
            key: 'lastmodified',
            sortOrder: sortedInfo?.columnKey === 'lastmodified' ? sortedInfo?.sortingDirection as SortOrder : undefined,
            width: 150,
            sorter: { compare: (a, b) => sortDate(a.lastModified, b.lastModified) },
            render: (_, member: WebApiMemberDtoMemberDto) => (
                <span>{formatDateString(member?.lastModified!.toString())}<br />
                </span>
            ),
        },
            {
                title: GetText("MemberList_CreatedColumn"),
                dataIndex: 'createdat',
                key: 'createdat',
                sortOrder: sortedInfo?.columnKey === 'createdat' ? sortedInfo?.sortingDirection as SortOrder : undefined,
                width: 150,
                sorter: { compare: (a, b) => sortDate(a.createdAt, b.createdAt) },
                render: (_, member: WebApiMemberDtoMemberDto) => (
                    <span>{formatDateString(member?.createdAt!.toString())}<br />
                    </span>
                ),
            },
            {
                title: GetText("MemberList_ActionColumn"),
                key: 'action',
                width: 150,
                onCell: () => ({ onClick: (e: React.MouseEvent) => e.stopPropagation() }),
                render: (_, member) => (
                    <Space size="middle">
                        <MemberEditModal user={props.user} membershipProducts={props.membershipProducts ?? []} memberFieldSettings={props.memberFieldSettings ?? []} loadMembers={props.loadMembers ?? (() => { })} members={props.members ?? []} groups={props.groups} member={member} hasPingPaymentIntegration={props.hasPingPaymentIntegration} />
                        <ManagePayments loadMembers={props.loadMembers} membershipProducts={props.membershipProducts} member={member} hasPingPaymentIntegration={props.hasPingPaymentIntegration} hasFortnoxIntegration={props.hasFortnoxIntegration} hasYearlyPayments={props.user?.paymentConfiguration === PaymentConfiguration.YearlyPayments}></ManagePayments>

                        {!props.user.hasOnlyReadRights ? <DeleteOutlined data-testid="delete-member-button" style={{ color: "red", fontSize: "20px" }} onClick={() => onDeleteClick(member?.memberId!)}></DeleteOutlined> : ""}

                        <Popconfirm
                            title={GetText("MemberList_DeleteMemberConfirmMessage")}
                            key={member.email}
                            open={member.memberId === memberToDelete}
                            placement="topRight"
                            onConfirm={handleOk}
                            onCancel={() => setMemberToDelete(0)}
                        >
                        </Popconfirm>
                    </Space>
                ),
            },)
    }

    return (<>
        <Table
            columns={columns}
            style={props.style}
            locale={{ emptyText: GetText("MemberList_EmptyMessage") }}
            className="admin-table"
            rowKey={m => m.memberId!}
            scroll={{ x: 'max-content' }}
            showSorterTooltip={false}
            components={components}
            onChange={onTableChange}
            sortDirections={['ascend', 'descend', 'ascend']}
            pagination={{ pageSize: props.pageSizePreference, locale: { items_per_page: "/ " + GetText("Common_Page") }, position: ['topRight'], showSizeChanger: true, onShowSizeChange: onPaginateChange }}
            dataSource={props.members}
            rowClassName={(record: WebApiMemberDtoMemberDto, index) => record.status == 1 ? "cancelled-member" : ""}
            onRow={props.enableRowClick ? (record: WebApiMemberDtoMemberDto) => ({
                onClick: () => {
                    navigate(`/${getCurrentLanguage()}/users/${record.memberId}`);
                },
                style: { cursor: 'pointer' }
            }) : undefined}
        />
        <CustomListEditModal
            open={editModalOpen}
            onClose={() => setEditModalOpen(false)}
            onSuccess={() => setEditModalOpen(false)}
            customFields={customFieldsForModal}
            item={editItem!}
            hasOnlyReadRights={props.user?.hasOnlyReadRights}
        />
        <CustomListAddModal
            open={addModalOpen}
            member={modalMember ?? undefined}
            onClose={() => setAddModalOpen(false)}
            onSuccess={() => setAddModalOpen(false)}
            customFields={customFieldsForModal}
            customListId={modalCustomListId || 1}
            hasOnlyReadRights={props.user?.hasOnlyReadRights}
        />
    </>);
}